import { ButtonGroup } from "@/components/ButtonGroup";
import { useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { PlayerContentSelect } from "./PlayerContentSelect";
import { PlayerContentGroupSelect } from "./PlayerContentGroupSelect";
import { PlayerChannelSelect } from "./PlayerChannelSelect";
import { FormLabel, FormMessage } from "@/components/ui/form";
import { SelectedMediaPreview } from "@/pages/shared/SelectedMediaPreview";
import { XIcon } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

type AssignableMediaTypes = "channel" | "contentGroup" | "content";

export const PlayerMediaSelect: React.FC<{
  form: UseFormReturn<any>;
  visibleTypes: AssignableMediaTypes[];
  label?: string;
  properties: {
    channelId?: string;
    contentGroupId?: string;
    contentId?: string;
  };
  defaultScreenSizeGuid?: string;
}> = ({ form, visibleTypes, label, properties, defaultScreenSizeGuid }) => {
  const { t } = useTranslation();
  const [selectedMediaType, setSelectedMediaType] = useState<AssignableMediaTypes>();
  const [showChannelSelect, setShowChannelSelect] = useState(false);
  const [showContentGroupSelect, setShowContentGroupSelect] = useState(false);
  const [showContentSelect, setShowContentSelect] = useState(false);

  const channelIdPropKey = properties.channelId || "";
  const contentGroupIdPropKey = properties.contentGroupId || "";
  const contentIdPropKey = properties.contentId || "";

  useEffect(() => {
    if (form.getValues(channelIdPropKey)) {
      setSelectedMediaType("channel");
    }
  }, [channelIdPropKey, form.getValues(channelIdPropKey)]);

  useEffect(() => {
    if (form.getValues(contentIdPropKey)) {
      setSelectedMediaType("content");
    }
  }, [contentIdPropKey, form.getValues(contentIdPropKey)]);

  useEffect(() => {
    if (form.getValues(contentGroupIdPropKey)) {
      setSelectedMediaType("contentGroup");
    }
  }, [contentGroupIdPropKey, form.getValues(contentGroupIdPropKey)]);

  const buttons: React.ComponentProps<typeof ButtonGroup>["items"] = visibleTypes.map((type) => ({
    id: type,
    text: t(`entities.${type}`),
  }));

  if (selectedMediaType) {
    buttons.push({
      id: "clear" as any,
      icon: (
        <Tooltip>
          <TooltipTrigger>
            <XIcon className="w-4 h-4" />
          </TooltipTrigger>
          <TooltipContent>{t("common.reset")}</TooltipContent>
        </Tooltip>
      ),
    });
  }

  return (
    <div>
      <FormLabel>{label || t("common.media")}</FormLabel>
      <div className="mt-3">
        <ButtonGroup
          items={buttons}
          selectedId={selectedMediaType}
          onClick={(id) => {
            setShowChannelSelect(id === "channel");
            setShowContentGroupSelect(id === "contentGroup");
            setShowContentSelect(id === "content");
            if (id === "clear") {
              form.setValue(channelIdPropKey, undefined);
              form.setValue(contentGroupIdPropKey, undefined);
              form.setValue(contentIdPropKey, undefined);
              setSelectedMediaType(undefined);
            }
          }}
        />
      </div>
      {showChannelSelect && (
        <PlayerChannelSelect
          value={form.getValues(channelIdPropKey) || undefined}
          defaultScreenSizeGuid={defaultScreenSizeGuid}
          onChange={(intId) => {
            if (visibleTypes.includes("channel")) form.setValue(channelIdPropKey, intId);
            if (visibleTypes.includes("contentGroup")) form.setValue(contentGroupIdPropKey, undefined);
            if (visibleTypes.includes("content")) form.setValue(contentIdPropKey, undefined);

            setShowChannelSelect(false);
          }}
          onOpenChange={() => setShowChannelSelect(false)}
        />
      )}
      {showContentGroupSelect && (
        <PlayerContentGroupSelect
          value={form.getValues(contentGroupIdPropKey) || undefined}
          defaultScreenSizeGuid={defaultScreenSizeGuid}
          onChange={(intId) => {
            if (visibleTypes.includes("channel")) form.setValue(channelIdPropKey, undefined);
            if (visibleTypes.includes("content")) form.setValue(contentIdPropKey, undefined);
            if (visibleTypes.includes("contentGroup")) form.setValue(contentGroupIdPropKey, intId);
            setShowContentGroupSelect(false);
          }}
          onOpenChange={() => setShowContentGroupSelect(false)}
        />
      )}
      {showContentSelect && (
        <PlayerContentSelect
          onChange={(intId) => {
            if (visibleTypes.includes("channel")) form.setValue(channelIdPropKey, undefined);
            if (visibleTypes.includes("contentGroup")) form.setValue(contentGroupIdPropKey, undefined);
            if (visibleTypes.includes("content")) form.setValue(contentIdPropKey, intId);
            setShowContentSelect(false);
          }}
          onOpenChange={() => setShowContentSelect(false)}
          defaultScreenSizeGuid={defaultScreenSizeGuid}
        />
      )}
      <SelectedMediaPreview
        contentIntId={form.getValues(contentIdPropKey) || undefined}
        contentGroupIntId={form.getValues(contentGroupIdPropKey) || undefined}
        channelIntId={form.getValues(channelIdPropKey) || undefined}
      />
      <FormMessage />
    </div>
  );
};
